/**
 * Floats Design System Tokens
 * ===========================
 *
 * Physics Model:
 * - Light source: top-left
 * - Shadows: soft, diffuse, blue-tinted
 * - Hierarchy: tone + spacing > borders > shadows
 */

// =============================================================================
// PRIMITIVE COLOR SCALES
// =============================================================================

export const primitives = {
  navy: {
    50: '#EEF2FF',
    100: '#E0E7FF',
    200: '#C7D2FE',
    300: '#A5B4FC',
    400: '#7B8FEB',
    500: '#4F6AE5',
    600: '#3251D0',
    700: '#2541B2',
    800: '#1B3194',
    900: '#0F1D5C',
  },
  indigo: {
    50: '#e8ecfb',
    100: '#ccd5f5',
    200: '#9bacea',
    300: '#6b84da',
    400: '#3b5cc9',
    500: '#1a41b8',
    600: '#13319a',
    700: '#11298a',
    800: '#0e2478',
    900: '#0a1a52',
  },
  pink: {
    50: '#fde8f1',
    100: '#fbd0e2',
    200: '#f6a1c5',
    300: '#f06fa9',
    400: '#ea3d8d',
    500: '#e50b71',
    600: '#c8095f',
    700: '#b20859',
    800: '#8f0747',
    900: '#6b0535',
  },
  purple: {
    50: '#F7F3FD',
    100: '#E9E0FB',
    200: '#D1C1F6',
    300: '#B79CF1',
    400: '#9873EB',
    500: '#7D4CE5',
    600: '#682FDD',
    700: '#5725BE',
    800: '#461E99',
    900: '#351773',
  },
  neutral: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },
} as const;

// =============================================================================
// SEMANTIC COLOR TOKENS
// =============================================================================

export const colors = {
  // Canvas Colors
  canvas: {
    default: '#FFFFFF',        // White background - app canvas
    card: '#fafafa',           // Light gray - cards and surfaces
  },

  // Text Colors
  text: {
    primary: '#111827',        // Ink - never pure black
    muted: '#6B7280',          // Secondary copy, labels
    disabled: 'rgba(17, 24, 39, 0.35)',
    inverse: '#FFFFFF',        // For dark backgrounds
  },

  // Action Colors (Two-tier system)
  action: {
    // Accent (pink) - high-visibility CTAs
    accent: '#e50b71',
    accentHover: '#c8095f',
    accentPressed: '#b20859',

    // Product commitment (primary blue) - in-app primary actions
    product: '#13319a',
    productHover: '#11298a',
    productPressed: '#0e2478',
    productLight: 'rgba(19, 49, 154, 0.08)', // For active states

    // Submission product identity
    submission: '#682FDD',
    submissionHover: '#5725BE',
    submissionPressed: '#461E99',
    submissionLight: '#F7F3FD',
  },

  // Signal Colors (Restrained - data only)
  signal: {
    success: '#16A34A',
    successLight: 'rgba(22, 163, 74, 0.12)',
    warning: '#F59E0B',
    warningLight: 'rgba(245, 158, 11, 0.12)',
    danger: '#EF4444',
    dangerLight: 'rgba(239, 68, 68, 0.12)',
    info: '#2563EB',
    infoLight: 'rgba(37, 99, 235, 0.12)',
  },

  // Border & Divider Colors
  border: {
    subtle: '#E2E8F0',         // Default structure
    strong: '#CBD5E1',         // Tables only
    focus: '#13319a',          // Focus rings
    input: 'rgba(0, 0, 0, 0.23)',       // Input default border
    inputHover: '#131313',              // Input hover border
  },
  divider: 'rgba(17, 24, 39, 0.08)',

  // Focus Ring
  focus: {
    stroke: '#13319a',
    ring: 'rgba(19, 49, 154, 0.24)',
  },

  // Overlay
  overlay: {
    scrim: 'rgba(17, 24, 39, 0.55)',
  },

  // Periwinkle wash (interactive zones)
  periwinkle: {
    bg: '#E8E7FF',
    bgSubtle: 'rgba(232, 231, 255, 0.5)',
  },

  // Dashboard Colors
  dashboard: {
    // Solid colors
    navyPrimary: '#13319A',      // Welcome panel gradient start
    navyDark: '#0B082F',         // Welcome panel gradient end
    iosBlue: '#007AFF',          // Blue metric card start
    deepBlue: '#003299',         // Blue metric card end
    pink: '#E50B71',             // Pink metric card start
    purple: '#9747FF',           // Pink metric card end
    gold: '#FFB825',             // Orange metric card start, caption badge
    orangeRed: '#FF4400',        // Orange metric card end
    grey: '#939393',             // Job title text
    darkGrey: '#555555',         // Caption text
    uploadBg: '#E8ECFB',         // CV upload background
    // List colors (notifications, leads)
    purpleLight: '#F9E8FF',      // Unread notification background/border
    purpleMain: '#DDD5FF',       // Default row border color
  },
} as const;

// Dashboard Gradients
export const gradients = {
  welcomePanel: 'linear-gradient(90deg, #13319A 0%, #0B082F 100%)',
  captionBadge: 'linear-gradient(90deg, #FFB825 0%, #E50B71 100%)',
  metricBlue: 'linear-gradient(180deg, #007AFF 0%, #003299 100%)',
  metricPink: 'linear-gradient(180deg, #E50B71 0%, #9747FF 100%)',
  metricOrange: 'linear-gradient(180deg, #FFB825 0%, #FF4400 100%)',
} as const;

// =============================================================================
// GEOMETRY TOKENS
// =============================================================================

export const radius = {
  sm: 4,    // Controls, compact containers
  md: 8,   // Inputs, buttons, menus
  lg: 16,   // Cards, modals, panels
  xl: 32,  // Dashboard, hero elements
  pill: 999, // Tags/badges only
} as const;

// =============================================================================
// SPACING TOKENS (4pt base, 8pt rhythm)
// =============================================================================

export const spacing = {
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 24,
  6: 32,
  7: 40,
  8: 48,
  9: 64,
  10: 80,
} as const;

// Named spacing for common use cases
export const layout = {
  pageGutterDesktop: 64,
  pageGutterTablet: 32,
  pageGutterMobile: 16,
  cardPadding: 24,       // Law for Float Card
  modalPadding: 32,      // Law for Modal
  formRowGap: 16,
  sectionGap: 48,
  chatColumnWidth: 768,   // Chat message + input column max-width
} as const;

// =============================================================================
// ELEVATION TOKENS (Shadows)
// Layered shadows: ambient + key light for realistic depth
// =============================================================================

export const shadows = {
  0: 'none',
  // Level 1: Resting - subtle lift (buttons at rest)
  1: '0 1px 3px rgba(27, 49, 148, 0.12), 0 1px 2px rgba(27, 49, 148, 0.24)',
  // Level 2: Cards - visible depth for surfaces
  2: '0 3px 6px rgba(27, 49, 148, 0.15), 0 2px 4px rgba(27, 49, 148, 0.12)',
  // Level 3: Hover/dropdowns - interactive elevation
  3: '0 10px 20px rgba(27, 49, 148, 0.15), 0 3px 6px rgba(27, 49, 148, 0.10)',
  // Level 4: Dialogs - prominent separation
  4: '0 15px 25px rgba(27, 49, 148, 0.15), 0 5px 10px rgba(27, 49, 148, 0.05)',
  // Level 5: Popovers/max - deepest elevation
  5: '0 20px 40px rgba(27, 49, 148, 0.20), 0 8px 16px rgba(27, 49, 148, 0.10)',
  // Specialty shadows
  inset: 'inset 0 2px 4px rgba(27, 49, 148, 0.12)',
  focus: '0 0 0 2px #13319a', // Focus ring for inputs - 2px solid primary
  focusButton: '0 0 0 3px rgba(19, 49, 154, 0.24)', // Focus ring for buttons - 3px with offset
  primary: '0 4px 14px rgba(19, 49, 154, 0.35)',
  primaryHover: '0 6px 20px rgba(19, 49, 154, 0.45)',
  accent: '0 4px 14px rgba(229, 11, 113, 0.35)',
  accentHover: '0 6px 20px rgba(229, 11, 113, 0.45)',
} as const;

// =============================================================================
// SIZING TOKENS
// =============================================================================

export const sizing = {
  controlHeight: 44,      // Default inputs/buttons
  controlHeightLg: 48,    // Hero CTAs, landing
  controlHeightSm: 36,    // Dense/compact
  iconSm: 16,
  iconMd: 20,
  iconLg: 24,
  sidebarWidth: 260,
  maxContentWidth: 1200,
} as const;

// =============================================================================
// TYPOGRAPHY TOKENS
// =============================================================================

export const typography = {
  fontFamily: {
    sans: 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    heading: 'Poppins, Inter, ui-sans-serif, system-ui, sans-serif',
    mono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  },

  fontWeight: {
    light: 300,     // Body text (body1, body2)
    regular: 400,   // Headings (h1, h2, h4, h5), buttons
    medium: 500,    // Emphasized text (h3, subtitle1)
  },

  // [fontSize, lineHeight]
  scale: {
    xs: [12, 16],
    sm: [14, 20],
    md: [16, 24],
    lg: [20, 28],
    xl: [24, 32],
    '2xl': [32, 40],
    '3xl': [44, 52],  // Hero headlines
  },

  // Label style (for STEP, section headers)
  label: {
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    fontWeight: 500,
    fontSize: 11,
  },
} as const;

// =============================================================================
// MOTION TOKENS
// =============================================================================

export const motion = {
  duration: {
    fast: 120,
    normal: 180,
    slow: 240,
  },
  easing: {
    standard: 'cubic-bezier(0.2, 0, 0, 1)',
    enter: 'cubic-bezier(0, 0, 0.2, 1)',
    exit: 'cubic-bezier(0.4, 0, 1, 1)',
  },
} as const;

// =============================================================================
// Z-INDEX TOKENS
// =============================================================================

export const zIndex = {
  base: 0,
  dropdown: 100,
  sticky: 200,
  modal: 300,
  popover: 400,
  tooltip: 500,
  toast: 600,
} as const;

// =============================================================================
// COMPONENT-SPECIFIC TOKENS
// =============================================================================

export const components = {
  button: {
    height: sizing.controlHeight,
    heightLg: sizing.controlHeightLg,
    paddingX: spacing[4],
    paddingXLg: spacing[5],
    radius: radius.md,
    fontSize: 15,
    fontWeight: typography.fontWeight.medium,
    letterSpacing: '0.2px',
  },

  input: {
    height: sizing.controlHeight,
    paddingX: spacing[4],
    radius: radius.md,
    borderWidth: 1,
    focusRingWidth: 2,
  },

  card: {
    padding: layout.cardPadding,
    radius: radius.lg,
  },

  modal: {
    padding: layout.modalPadding,
    radius: radius.lg,
  },

  tag: {
    radius: radius.pill,
    paddingX: spacing[3],
    paddingY: spacing[2],
    fontSize: typography.scale.xs[0],
  },

  sidebar: {
    width: sizing.sidebarWidth,
    itemHeight: 44,
    activeBarWidth: 4,
  },
} as const;

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type ColorToken = typeof colors;
export type RadiusToken = typeof radius;
export type SpacingToken = typeof spacing;
export type ShadowToken = typeof shadows;
export type SizingToken = typeof sizing;
export type TypographyToken = typeof typography;
export type MotionToken = typeof motion;

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/** Convert spacing token to px string */
export const sp = (token: keyof typeof spacing): string => `${spacing[token]}px`;

/** Convert radius token to px string */
export const rd = (token: keyof typeof radius): string => `${radius[token]}px`;

/** Get typography scale as CSS object */
export const text = (scale: keyof typeof typography.scale) => {
  const [fontSize, lineHeight] = typography.scale[scale];
  return {
    fontSize: `${fontSize}px`,
    lineHeight: `${lineHeight}px`,
  };
};

// =============================================================================
// TEXT STYLE PRESETS
// =============================================================================

/**
 * Complete text style objects matching MUI theme typography.
 * Use by spreading into style props: style={{ ...textStyles.h1 }}
 */
export const textStyles = {
  h1: {
    fontFamily: 'var(--font-heading)',
    fontSize: `${typography.scale['2xl'][0]}px`,
    lineHeight: `${typography.scale['2xl'][1]}px`,
    fontWeight: typography.fontWeight.regular,
  },
  h2: {
    fontFamily: 'var(--font-heading)',
    fontSize: `${typography.scale.xl[0]}px`,
    lineHeight: `${typography.scale.xl[1]}px`,
    fontWeight: typography.fontWeight.regular,
  },
  h3: {
    fontFamily: 'var(--font-heading)',
    fontSize: `${typography.scale.lg[0]}px`,
    lineHeight: `${typography.scale.lg[1]}px`,
    fontWeight: typography.fontWeight.medium,
  },
  h4: {
    fontFamily: 'var(--font-sans)',
    fontSize: '15px', // Between sm and md
    lineHeight: '22px',
    fontWeight: typography.fontWeight.regular,
  },
  h5: {
    fontFamily: 'var(--font-sans)',
    fontSize: `${typography.scale.xs[0]}px`,
    lineHeight: `${typography.scale.xs[1]}px`,
    fontWeight: typography.fontWeight.regular,
  },
  h6: {
    fontFamily: 'var(--font-heading)',
    fontSize: `${typography.label.fontSize}px`,
    lineHeight: `${typography.scale.xs[1]}px`,
    fontWeight: typography.fontWeight.regular,
  },
  subtitle1: {
    fontFamily: 'var(--font-sans)',
    fontSize: `${typography.scale.md[0]}px`,
    lineHeight: `${typography.scale.md[1]}px`,
    fontWeight: typography.fontWeight.medium,
  },
  body1: {
    fontFamily: 'var(--font-sans)',
    fontSize: `${typography.scale.sm[0]}px`,
    lineHeight: `${typography.scale.sm[1]}px`,
    fontWeight: typography.fontWeight.light,
  },
  body2: {
    fontFamily: 'var(--font-sans)',
    fontSize: `${typography.scale.sm[0]}px`,
    lineHeight: `${typography.scale.sm[1]}px`,
    fontWeight: typography.fontWeight.light,
  },
  button: {
    fontFamily: 'var(--font-sans)',
    fontSize: '15px', // Between sm and md
    lineHeight: '22px',
    fontWeight: typography.fontWeight.regular,
  },
  caption: {
    fontFamily: 'var(--font-sans)',
    fontSize: `${typography.scale.xs[0]}px`,
    lineHeight: `${typography.scale.xs[1]}px`,
    fontWeight: typography.fontWeight.regular,
  },
  label: {
    fontFamily: 'var(--font-heading)',
    fontSize: `${typography.label.fontSize}px`,
    lineHeight: '14px',
    fontWeight: typography.fontWeight.regular,
    textTransform: typography.label.textTransform,
    letterSpacing: typography.label.letterSpacing,
  },
} as const;

/**
 * Mobile heading sizes (80% of desktop). Use at md breakpoint and below.
 * Only includes headings — body/button/caption styles are unchanged.
 * Use by spreading: style={{ ...textStyles.h1, ...textStylesMobile.h1 }}
 */
export const textStylesMobile = {
  h1: { fontSize: '26px', lineHeight: '32px' },
  h2: { fontSize: '19px', lineHeight: '26px' },
  h3: { fontSize: '16px', lineHeight: '22px' },
  h4: { fontSize: '12px', lineHeight: '18px' },
  h5: { fontSize: '10px', lineHeight: '13px' },
  h6: { fontSize: '9px', lineHeight: '13px' },
} as const;

export type TextStyle = keyof typeof textStyles;
export type TextStyleMobile = keyof typeof textStylesMobile;
